import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Typography } from "@mui/material";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import JSZip from "jszip";
import React, { useEffect, useState } from "react";
import { CustomActionIconLink } from "../../components/CustomActionIconLink/CustomActionIconLink";
import DataTable, {
  ColumnDef,
  SortOrder,
} from "../../components/DataTable/DataTable";
import { CertificateView } from "./components/CertificateView";
import { usePageMetadata } from "../../context/PageMetadataContext";
import { useAuth } from "../../context/AuthContext";
import { usePortal } from "../../context/PortalsContext";
import { useToast } from "../../context/ToastContext";
import { getCertificates } from "../../services/api/apiClient";
import {
  UserCertificateResult,
  UserCertificatesPaginatedResult,
  CertificateQueryParams,
} from "../../services/api/types/certificates/certificates";
import { brand } from "../../theme/themePrimitives";
import { resolvePageStyle } from "../../utils/pageStyleResolver";
import { useCustomisation } from "../../context/CustomisationContext";
import { extractErrorMessage } from "../../utils/errorUtils";
import { hasSomeCustomisation } from "../../utils/helpers";
import { usePageCustomisation } from "../../hooks/usePageCustomisation";
import { CertificatesSkeleton } from "./components/CertificatesSkeleton";

export const Certificates: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [certificatesResp, setCertificatesResp] =
    useState<UserCertificatesPaginatedResult>();
  const [orderBy, setOrderBy] = useState<string>("assignment");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [selectedCertificates, setSelectedCertificates] = useState<
    UserCertificateResult[]
  >([]);
  const [certificateForPDF, setCertificateForPDF] =
    useState<UserCertificateResult | null>(null);
  const { auth } = useAuth();
  const { portals } = usePortal();
  usePageCustomisation("CERTIFICATES");
  const { showToast } = useToast();

  const { customisations, getCustomisationValue } = useCustomisation();

  const {
    CERTIFICATES: { buttonsSettings: certificatesButtonssettings },
    LOGIN: { buttonsSettings: loginButtonSettings },
  } = customisations.customisations;
  const linkTextColor =
    certificatesButtonssettings?.certificatesLinkColor ??
    loginButtonSettings?.loginLinkColor;

  const certificatesPageStyle = resolvePageStyle(
    "CERTIFICATES",
    getCustomisationValue,
  );

  const anySelected = selectedCertificates.length > 0;

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        if (auth.user && portals.selectedPortal) {
          setLoading(true);
          const params: CertificateQueryParams = {
            limit,
            offset: (page - 1) * limit,
            search: search || undefined,
            orderBy,
            sort: sortOrder,
          };

          const response = await getCertificates(
            portals.selectedPortal.portalUid,
            auth.user.UserPortalUid,
            params,
          );
          setCertificatesResp(response);
        }
      } catch (error: any) {
        console.log("Error:", error);

        showToast(
          extractErrorMessage(error, "Failed to fetch certificates"),
          "error",
        );
        setCertificatesResp(undefined);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.user,
    portals.selectedPortal,
    limit,
    page,
    search,
    orderBy,
    sortOrder,
  ]);

  const handleSortChange = (newOrderBy: string, newOrder: SortOrder) => {
    setOrderBy(newOrderBy);
    setSortOrder(newOrder);
    setPage(1);
  };

  // Format date helper
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateString;
    }
  };

  // Format time spent (number in minutes to string)
  const formatTimeSpent = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0
      ? `${hours} hour${hours !== 1 ? "s" : ""} ${mins} min${
          mins !== 1 ? "s" : ""
        }`
      : `${hours} hour${hours !== 1 ? "s" : ""}`;
  };

  // Format best score (number to percentage string)
  const formatBestScore = (score: number): string => {
    return `${score}%`;
  };

  const generateCertificatePDF = async (
    certificate: UserCertificateResult,
  ): Promise<{ blob: Blob; filename: string }> => {
    const certificateId = certificate.certificateUid;
    const filename = `${certificate.assignment.replace(
      /[^a-z0-9]/gi,
      "_",
    )}_certificate.pdf`;

    // Set the certificate to render in the DOM (similar to Reports approach)
    setCertificateForPDF(certificate);

    // Wait for React to render the component in the DOM
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Get the element from the DOM using getElementById (same as Reports)
      const element = document.getElementById(certificateId);
      if (!element) {
        throw new Error(
          `Certificate element not found with ID: ${certificateId}`,
        );
      }

      // Wait for all images to load
      const images = element.querySelectorAll("img");
      const imagePromises = Array.from(images).map((img) => {
        if (img.complete && img.naturalWidth > 0) {
          return Promise.resolve();
        }
        return new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => {
            console.warn("Image failed to load:", img.src);
            resolve(); // Continue even if image fails
          };
          // Timeout after 5 seconds
          setTimeout(() => {
            console.warn("Image load timeout:", img.src);
            resolve();
          }, 5000);
        });
      });

      await Promise.all(imagePromises);
      // Additional small delay to ensure rendering is complete
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Use html2canvas with same options as Reports (similar approach)
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("l", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = (canvas.height * pageWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);

      const blob = pdf.output("blob");

      // Clear the certificate from state
      setCertificateForPDF(null);

      return { blob, filename };
    } catch (error) {
      // Clear the certificate from state on error
      setCertificateForPDF(null);
      throw error;
    }
  };

  const downloadCertificatePDF = async (certificate: UserCertificateResult) => {
    const { blob, filename } = await generateCertificatePDF(certificate);
    saveAs(blob, filename);
  };

  const handleBulkDownload = async () => {
    if (selectedCertificates.length === 0) return;

    try {
      if (selectedCertificates.length === 1) {
        await downloadCertificatePDF(selectedCertificates[0]);
        return;
      }

      const zip = new JSZip();

      for (let i = 0; i < selectedCertificates.length; i++) {
        const certificate = selectedCertificates[i];
        const { blob, filename } = await generateCertificatePDF(certificate);

        zip.file(filename, blob);

        if (i < selectedCertificates.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFilename = `certificates_${new Date().getTime()}.zip`;
      saveAs(zipBlob, zipFilename);
    } catch (error) {
      console.error("Error downloading certificates:", error);
    }
  };

  usePageMetadata({
    title: "Certificates",
    breadcrumbs: [],
    pageStyle: certificatesPageStyle,
  });

  const columns: ColumnDef<UserCertificateResult>[] = [
    {
      id: "assignment",
      label: "Assignment",
      sortable: true,
      render: (certificate) => (
        <Typography
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            downloadCertificatePDF(certificate);
          }}
          sx={{
            background: "none",
            border: "none",
            color: linkTextColor ?? brand[600],
            textDecoration: "underline",
            cursor: "pointer",
            padding: 0,
            fontSize: "inherit",
            fontFamily: "inherit",
            fontWeight: 600,
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {certificate.assignment}
        </Typography>
      ),
    },
    {
      id: "type",
      label: "Type",
      sortable: true,
      getValue: (certificate) =>
        certificate.type
          ? certificate.type.charAt(0).toUpperCase() + certificate.type.slice(1)
          : "",
    },
    {
      id: "startDate",
      label: "Start date",
      sortable: true,
      getValue: (certificate) => formatDate(certificate.startDate),
    },
    {
      id: "completionDate",
      label: "Completion date",
      sortable: true,
      getValue: (certificate) => formatDate(certificate.completionDate),
    },
    {
      id: "timeSpent",
      label: "Time spent",
      sortable: true,
      getValue: (certificate) => formatTimeSpent(certificate.timeSpent),
    },
    {
      id: "bestScore",
      label: "Best score",
      sortable: true,
      getValue: (certificate) => formatBestScore(certificate.bestScore),
    },
  ];

  const isCertificatesLoaded = hasSomeCustomisation(
    customisations.customisations,
    "CERTIFICATES",
  );

  if (!isCertificatesLoaded) return <CertificatesSkeleton />;

  return (
    <>
      {certificateForPDF && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            width: "100vw",
            height: "100vh",
            overflow: "hidden",
            opacity: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#2B2B2B",
              minHeight: "100vh",
              py: 6,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CertificateView certificate={certificateForPDF} />
          </Box>
        </Box>
      )}
      <Box sx={{ maxWidth: 1440, margin: "0 auto" }}>
        <DataTable
          data={certificatesResp?.rows ?? []}
          columns={columns}
          totalCount={certificatesResp?.count ?? 0}
          page={page}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(rows) => {
            setLimit(rows);
            setPage(1);
          }}
          sort={{ orderBy, order: sortOrder }}
          onSortChange={handleSortChange}
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          searchPlaceholder="Search by name"
          isLoading={loading}
          enableSelection={true}
          selectedRows={selectedCertificates}
          onSelectionChange={setSelectedCertificates}
          getRowId={(certificate) => certificate.certificateUid}
          selectedCountTitle="Selected certificates"
          actions={
            anySelected ? (
              <CustomActionIconLink
                title="Download"
                icon={<FileDownloadOutlinedIcon />}
                onClick={handleBulkDownload}
                linkColor={linkTextColor}
              />
            ) : undefined
          }
        />
      </Box>
    </>
  );
};
